import type { FC, ReactNode } from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  DEFAULT_FEATURE_FLAGS,
  type FeatureFlags,
  FeatureFlagsContext,
  type FeatureFlagsContextType,
} from "./context";

const featureFlagKeys = Object.keys(
  DEFAULT_FEATURE_FLAGS,
) as (keyof FeatureFlags)[];

const STORAGE_KEY = "speria-karte-feature-flags";

type Props = {
  children: ReactNode;
};

export const FeatureFlagsProvider: FC<Props> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [loaded, setLoaded] = useState(false);

  // localStorage からフラグを読み込み
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedFlags = JSON.parse(stored) as Partial<
          Record<string, boolean>
        >;
        setFlags((prev) => {
          const newFlags = { ...prev };
          for (const key of featureFlagKeys) {
            if (key in parsedFlags && typeof parsedFlags[key] !== "undefined") {
              newFlags[key] = parsedFlags[key];
            }
          }
          return newFlags;
        });
      }
    } catch (error) {
      console.warn("Failed to load feature flags from localStorage:", error);
    } finally {
      setLoaded(true);
    }
  }, []);

  // localStorage にフラグを保存
  const saveToStorage = useCallback((newFlags: FeatureFlags) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFlags));
    } catch (error) {
      console.warn("Failed to save feature flags to localStorage:", error);
    }
  }, []);

  const updateFlag = useCallback(
    (key: keyof FeatureFlags, value: boolean) => {
      setFlags((prev) => {
        const newFlags = { ...prev, [key]: value };
        saveToStorage(newFlags);
        return newFlags;
      });
    },
    [saveToStorage],
  );

  const resetFlags = useCallback(() => {
    setFlags(DEFAULT_FEATURE_FLAGS);
    saveToStorage(DEFAULT_FEATURE_FLAGS);
  }, [saveToStorage]);

  const contextValue: FeatureFlagsContextType = {
    flags,
    loaded,
    updateFlag,
    resetFlags,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
    );
  }
  return context;
};
